import {useState, useEffect} from 'react'
import {supabase} from '../config/supabase'
import toast from 'react-hot-toast'

export const useBranding = () => {
  const [branding, setBranding] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchBranding()

    // Subscribe to branding changes
    const subscription = supabase
      .channel('branding-changes')
      .on('postgres_changes', {event: '*', schema: 'public', table: 'branding'}, () => fetchBranding())
      .subscribe()

    return () => subscription.unsubscribe()
  }, [])

  const fetchBranding = async () => {
    try {
      const {data, error} = await supabase
        .from('branding')
        .select('*')
        .order('updated_at', {ascending: false})
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      setBranding(data || getDefaultBranding())
    } catch (error) {
      console.error('Error fetching branding:', error)
      setBranding(getDefaultBranding())
    } finally {
      setLoading(false)
    }
  }

  const updateBranding = async (updates) => {
    try {
      setLoading(true)

      // Check if branding record exists
      const {data: existing} = await supabase
        .from('branding')
        .select('id')
        .limit(1)
        .single()

      let result
      if (existing) {
        // Update existing record
        result = await supabase
          .from('branding')
          .update(updates)
          .eq('id', existing.id)
          .select()
          .single()
      } else {
        // Create new record
        result = await supabase
          .from('branding')
          .insert([updates])
          .select()
          .single()
      }

      if (result.error) throw result.error

      setBranding(result.data)
      
      // Apply branding immediately
      applyBrandingToDOM(result.data)

      return {data: result.data, error: null}
    } catch (error) {
      console.error('Error updating branding:', error)
      toast.error('Failed to update branding')
      return {data: null, error}
    } finally {
      setLoading(false)
    }
  }

  return {branding, loading, updateBranding, refetch: fetchBranding}
}

const getDefaultBranding = () => ({
  id: 'default',
  logo_url: null,
  favicon_url: null,
  primary_color: '#399696',
  secondary_color: '#a0cfbc',
  accent_color: '#ffe8ad',
  heading_font: 'Inter',
  body_font: 'Inter',
  footer_links: [
    {label: 'Privacy Policy', url: '/privacy'},
    {label: 'Terms of Service', url: '/terms'},
    {label: 'Contact', url: 'mailto:laura@empowernutrition.info'}
  ]
})

// Apply branding to DOM
export const applyBrandingToDOM = (branding) => {
  if (!branding) return

  const root = document.documentElement

  // Apply CSS custom properties for colors
  root.style.setProperty('--color-primary', branding.primary_color)
  root.style.setProperty('--color-secondary', branding.secondary_color)
  root.style.setProperty('--color-accent', branding.accent_color)

  // Apply fonts
  if (branding.heading_font) {
    root.style.setProperty('--font-heading', branding.heading_font)
  }
  if (branding.body_font) {
    root.style.setProperty('--font-body', branding.body_font)
  }

  // Apply favicon
  if (branding.favicon_url) {
    let favicon = document.querySelector('link[rel="icon"]')
    if (!favicon) {
      favicon = document.createElement('link')
      favicon.rel = 'icon'
      document.head.appendChild(favicon)
    }
    favicon.href = branding.favicon_url
  }

  // Update Tailwind CSS variables
  updateTailwindColors(branding.primary_color, branding.secondary_color, branding.accent_color)
}

const updateTailwindColors = (primary, secondary, accent) => {
  // Convert hex to RGB for Tailwind CSS variables
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  const primaryRgb = hexToRgb(primary)
  const secondaryRgb = hexToRgb(secondary)
  const accentRgb = hexToRgb(accent)

  if (primaryRgb) {
    document.documentElement.style.setProperty(
      '--color-nutrition-600',
      `${primaryRgb.r} ${primaryRgb.g} ${primaryRgb.b}`
    )
    document.documentElement.style.setProperty(
      '--color-nutrition-700',
      `${Math.max(0, primaryRgb.r - 20)} ${Math.max(0, primaryRgb.g - 20)} ${Math.max(0, primaryRgb.b - 20)}`
    )
  }

  if (secondaryRgb) {
    document.documentElement.style.setProperty(
      '--color-nutrition-500',
      `${secondaryRgb.r} ${secondaryRgb.g} ${secondaryRgb.b}`
    )
    document.documentElement.style.setProperty(
      '--color-nutrition-100',
      `${Math.min(255, secondaryRgb.r + 50)} ${Math.min(255, secondaryRgb.g + 50)} ${Math.min(255, secondaryRgb.b + 50)}`
    )
  }

  if (accentRgb) {
    document.documentElement.style.setProperty(
      '--color-accent',
      `${accentRgb.r} ${accentRgb.g} ${accentRgb.b}`
    )
  }
}